import React, { useState, useMemo } from 'react';
import {
  Typography,
  useTheme,
  Card,
  CardContent,
  Box,
  Stack,
  Paper,
  Button,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import dayjs from 'dayjs';

interface CostCenterDataPoint {
  date: string;
  gcp: number;
  aws: number;
  azure: number;
  langfuse: number;
}

interface CostCenterChartProps {
  data: CostCenterDataPoint[];
}

const legendItems = [
    { name: 'GCP', color: 'rgba(0, 100, 255, 1)' },
    { name: 'AWS', color: '#FF8900' },
    { name: 'Azure', color: '#94E9B8' },
    { name: 'Langfuse', color: '#FF3023' }, // New Langfuse legend item
];

// --- Custom Tooltip for the Bar Chart ---
const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
        const isPlaceholder = payload.some(p => p.dataKey === 'placeholder' && typeof p.value === 'number' && p.value > 0);
        if (isPlaceholder) {
            return null;
        }

        const date = new Date(label as string);
        if (isNaN(date.getTime())) return null;
        
        const formattedDate = `${new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)}, ${date.getDate()} ${new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date)} • ${date.getFullYear()}`;
        
        const reorderedPayload = [...payload].reverse().filter(p => typeof p.value === 'number' && p.value > 0);

        return (
            <Paper elevation={3} sx={{ borderRadius: '8px', padding: '12px', minWidth: '180px', fontFamily: 'Inter' }}>
                <Typography sx={{ fontSize: '12px', color: '#888', marginBottom: '12px', textAlign: 'center' }}>
                    {formattedDate}
                </Typography>
                <Stack spacing={1}>
                    {reorderedPayload.map((pld, index) => (
                        <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FiberManualRecordIcon sx={{ color: pld.fill, fontSize: '10px', mr: 0.75 }} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{pld.name}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '12px', fontWeight: '500' }}>
                                {`$${(pld.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Paper>
        );
    }
    return null;
};

// --- Helper function to format large numbers for the Y-axis ---
const formatYAxisLabel = (num: number): string => {
    if (Math.abs(num) >= 1_000_000_000) {
      return `$${(num / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    }
    if (Math.abs(num) >= 1_000_000) {
      return `$${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (Math.abs(num) >= 1_000) {
      return `$${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return `$${num}`;
};

// --- Custom Y-Axis Tick Formatter ---
const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    if (!payload || payload.value === undefined) return null;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={-10} y={0} dy={4} textAnchor="end" fill="#00000066" style={{ fontSize: 13, fontFamily: 'Inter' }}>
          {formatYAxisLabel(payload.value)}
        </text>
      </g>
    );
};

const CostCenterChart: React.FC<CostCenterChartProps> = ({ data }) => {
    const theme = useTheme();
    const [selectedProvider, setSelectedProvider] = useState('All');

    const { processedData, yAxisDomain, yAxisTicks } = useMemo(() => {
        const MIN_BARS = 30;

        const maxTotalSpend = Math.max(
            ...data.map(item => (item.gcp || 0) + (item.aws || 0) + (item.azure || 0) + (item.langfuse || 0)),
            0
        );
        
        const yAxisTopValue = maxTotalSpend > 0 ? maxTotalSpend * 1.1 : 100;
        const minCeiling = Math.ceil(yAxisTopValue / 10) * 10;
        const tempInterval = minCeiling / 4;
        const niceInterval = Math.max(10, Math.floor(tempInterval / 10) * 10);
        
        const ticks = [];
        let currentTick = 0;
        if (niceInterval > 0) {
            while (currentTick < minCeiling) {
                ticks.push(currentTick);
                currentTick += niceInterval;
            }
            ticks.push(currentTick);
        } else {
             ticks.push(0, minCeiling);
        }
        
        const finalYAxisMax = ticks[ticks.length - 1] || 100;
        const domain = [0, finalYAxisMax];
        
        let finalData = data.map(item => ({ ...item, placeholder: 0, isPlaceholder: false }));
        const daysToAdd = MIN_BARS - finalData.length;

        if (finalData.length > 0 && daysToAdd > 0) {
            const lastDate = dayjs(finalData[finalData.length - 1]?.date);
            if (lastDate.isValid()) {
                for (let i = 1; i <= daysToAdd; i++) {
                    const minHeight = finalYAxisMax * 0.40;
                    const maxHeight = finalYAxisMax * 0.75;
                    finalData.push({
                        date: lastDate.add(i, 'day').format('YYYY-MM-DD'),
                        gcp: 0,
                        aws: 0,
                        azure: 0,
                        langfuse: 0,
                        placeholder: Math.random() * (maxHeight - minHeight) + minHeight,
                        isPlaceholder: true,
                    });
                }
            }
        }

        const filtered = finalData.map(item => {
            const mappedItem = {
                date: item.date,
                GCP: item.gcp,
                AWS: item.aws,
                Azure: item.azure,
                Langfuse: item.langfuse,
                placeholder: item.placeholder,
                isPlaceholder: item.isPlaceholder,
            };

            if (selectedProvider === 'All' || mappedItem.isPlaceholder) {
                return mappedItem;
            }
            return {
                ...mappedItem,
                GCP: selectedProvider === 'GCP' ? mappedItem.GCP : 0,
                AWS: selectedProvider === 'AWS' ? mappedItem.AWS : 0,
                Azure: selectedProvider === 'Azure' ? mappedItem.Azure : 0,
                Langfuse: selectedProvider === 'Langfuse' ? mappedItem.Langfuse : 0,
            };
        });

        return { processedData: filtered, yAxisDomain: domain, yAxisTicks: ticks };
    }, [data, selectedProvider]);

    const handleLegendClick = (name: string) => {
        setSelectedProvider(prev => (prev === name ? 'All' : name));
    };

    return (
        <Card elevation={0} sx={{ borderRadius: '16px', height: '400px', width: '100%', fontFamily: 'Inter, sans-serif' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px' }}>
                        Cost Center
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 }, flexWrap: 'wrap' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {legendItems.map((item) => (
                                <Button
                                    key={item.name}
                                    onClick={() => handleLegendClick(item.name)}
                                    size="small"
                                    sx={{
                                        borderRadius: '16px',
                                        textTransform: 'none',
                                        backgroundColor: selectedProvider === item.name ? theme.palette.primary.light + '33' : 'transparent',
                                        color: selectedProvider === item.name ? theme.palette.primary.main : 'text.secondary',
                                        '&:hover': {
                                            backgroundColor: selectedProvider === item.name ? theme.palette.primary.light + '55' : theme.palette.grey[500] + '1A',
                                        }
                                    }}
                                >
                                    <FiberManualRecordIcon sx={{ color: item.color, fontSize: '10px', mr: 0.75 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        {item.name}
                                    </Typography>
                                </Button>
                            ))}
                        </Stack>
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={processedData}
                            margin={{ top: 10, right: 10, left: 5, bottom: 0 }}
                            barCategoryGap="63%"
                        >
                            <CartesianGrid strokeDasharray="4 4" stroke={theme.palette.divider} horizontal={true} vertical={false} />
                            <XAxis dataKey="date" hide={true} />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                domain={yAxisDomain}
                                ticks={yAxisTicks}
                                tick={<CustomYAxisTick />}
                            />
                            <Tooltip
                                cursor={{ fill: 'transparent' }}
                                content={<CustomTooltip />}
                            />
                            <Bar dataKey="placeholder" stackId="a" fill={theme.palette.grey[200]} maxBarSize={15} />
                            <Bar dataKey="Langfuse" stackId="a" fill="#9333ea" maxBarSize={15} />
                            <Bar dataKey="Azure" stackId="a" fill="#94E9B8" maxBarSize={15} />
                            <Bar dataKey="AWS" stackId="a" fill="#FF8900" maxBarSize={15} />
                            <Bar dataKey="GCP" stackId="a" fill="rgba(0, 100, 255, 1)" maxBarSize={15} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CostCenterChart;




// import React, { useState, useMemo } from 'react';
// import {
//   Typography,
//   useTheme,
//   Card,
//   CardContent,
//   Box,
//   Stack,
//   Paper,
//   Button,
// } from '@mui/material';
// import {
//   ResponsiveContainer,
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   TooltipProps,
// } from 'recharts';
// import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
// import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
// import dayjs from 'dayjs';

// interface CostCenterDataPoint {
//   date: string;
//   gcp: number;
//   aws: number;
//   azure: number;
// }

// interface CostCenterChartProps {
//   data: CostCenterDataPoint[];
// }

// const legendItems = [
//     { name: 'GCP', color: 'rgba(0, 100, 255, 1)' },
//     { name: 'AWS', color: '#FF8900' },
//     { name: 'Azure', color: '#94E9B8' },
// ];

// // --- Custom Tooltip for the Bar Chart ---
// const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
//     if (active && payload && payload.length) {
//         const isPlaceholder = payload.some(p => p.dataKey === 'placeholder' && typeof p.value === 'number' && p.value > 0);
//         if (isPlaceholder) {
//             return null;
//         }

//         const date = new Date(label as string);
//         if (isNaN(date.getTime())) return null;
        
//         const formattedDate = `${new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)}, ${date.getDate()} ${new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date)} • ${date.getFullYear()}`;
        
//         const reorderedPayload = [...payload].reverse().filter(p => typeof p.value === 'number' && p.value > 0);

//         return (
//             <Paper elevation={3} sx={{ borderRadius: '8px', padding: '12px', minWidth: '180px', fontFamily: 'Inter' }}>
//                 <Typography sx={{ fontSize: '12px', color: '#888', marginBottom: '12px', textAlign: 'center' }}>
//                     {formattedDate}
//                 </Typography>
//                 <Stack spacing={1}>
//                     {reorderedPayload.map((pld, index) => (
//                         <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
//                             <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                                 <FiberManualRecordIcon sx={{ color: pld.fill, fontSize: '10px', mr: 0.75 }} />
//                                 <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{pld.name}</Typography>
//                             </Box>
//                             <Typography sx={{ fontSize: '12px', fontWeight: '500' }}>
//                                 {`$${(pld.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
//                             </Typography>
//                         </Stack>
//                     ))}
//                 </Stack>
//             </Paper>
//         );
//     }
//     return null;
// };

// // --- Helper function to format large numbers for the Y-axis ---
// const formatYAxisLabel = (num: number): string => {
//     if (Math.abs(num) >= 1_000_000_000) {
//       return `$${(num / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
//     }
//     if (Math.abs(num) >= 1_000_000) {
//       return `$${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
//     }
//     if (Math.abs(num) >= 1_000) {
//       return `$${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
//     }
//     return `$${num}`;
// };

// // --- Custom Y-Axis Tick Formatter ---
// const CustomYAxisTick = (props: any) => {
//     const { x, y, payload } = props;
//     if (!payload || payload.value === undefined) return null;
//     return (
//       <g transform={`translate(${x},${y})`}>
//         <text x={-10} y={0} dy={4} textAnchor="end" fill="#00000066" style={{ fontSize: 13, fontFamily: 'Inter' }}>
//           {formatYAxisLabel(payload.value)}
//         </text>
//       </g>
//     );
// };

// const CostCenterChart: React.FC<CostCenterChartProps> = ({ data }) => {
//     const theme = useTheme();
//     const [selectedProvider, setSelectedProvider] = useState('All');

//     const { processedData, yAxisDomain, yAxisTicks } = useMemo(() => {
//         const MIN_BARS = 30;

//         const maxTotalSpend = Math.max(
//             ...data.map(item => (item.gcp || 0) + (item.aws || 0) + (item.azure || 0)),
//             0
//         );
        
//         const yAxisTopValue = maxTotalSpend > 0 ? maxTotalSpend * 1.1 : 100;
//         const minCeiling = Math.ceil(yAxisTopValue / 10) * 10;
//         const tempInterval = minCeiling / 4;
//         const niceInterval = Math.max(10, Math.floor(tempInterval / 10) * 10);
        
//         const ticks = [];
//         let currentTick = 0;
//         if (niceInterval > 0) {
//             while (currentTick < minCeiling) {
//                 ticks.push(currentTick);
//                 currentTick += niceInterval;
//             }
//             ticks.push(currentTick);
//         } else {
//              ticks.push(0, minCeiling);
//         }
        
//         const finalYAxisMax = ticks[ticks.length - 1] || 100;
//         const domain = [0, finalYAxisMax];
        
//         let finalData = data.map(item => ({ ...item, placeholder: 0, isPlaceholder: false }));
//         const daysToAdd = MIN_BARS - finalData.length;

//         if (finalData.length > 0 && daysToAdd > 0) {
//             const lastDate = dayjs(finalData[finalData.length - 1]?.date);
//             if (lastDate.isValid()) {
//                 for (let i = 1; i <= daysToAdd; i++) {
//                     const minHeight = finalYAxisMax * 0.40;
//                     const maxHeight = finalYAxisMax * 0.75;
//                     finalData.push({
//                         date: lastDate.add(i, 'day').format('YYYY-MM-DD'),
//                         gcp: 0,
//                         aws: 0,
//                         azure: 0,
//                         placeholder: Math.random() * (maxHeight - minHeight) + minHeight,
//                         isPlaceholder: true,
//                     });
//                 }
//             }
//         }

//         const filtered = finalData.map(item => {
//             const mappedItem = {
//                 date: item.date,
//                 GCP: item.gcp,
//                 AWS: item.aws,
//                 Azure: item.azure,
//                 placeholder: item.placeholder,
//                 isPlaceholder: item.isPlaceholder,
//             };

//             if (selectedProvider === 'All' || mappedItem.isPlaceholder) {
//                 return mappedItem;
//             }
//             return {
//                 ...mappedItem,
//                 GCP: selectedProvider === 'GCP' ? mappedItem.GCP : 0,
//                 AWS: selectedProvider === 'AWS' ? mappedItem.AWS : 0,
//                 Azure: selectedProvider === 'Azure' ? mappedItem.Azure : 0,
//             };
//         });

//         return { processedData: filtered, yAxisDomain: domain, yAxisTicks: ticks };
//     }, [data, selectedProvider]);

//     const handleLegendClick = (name: string) => {
//         setSelectedProvider(prev => (prev === name ? 'All' : name));
//     };

//     return (
//         <Card elevation={0} sx={{ borderRadius: '16px', height: '400px', width: '100%', fontFamily: 'Inter, sans-serif' }}>
//             <CardContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
//                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
//                     <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px' }}>
//                         Cost Center
//                     </Typography>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 }, flexWrap: 'wrap' }}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                             {legendItems.map((item) => (
//                                 <Button
//                                     key={item.name}
//                                     onClick={() => handleLegendClick(item.name)}
//                                     size="small"
//                                     sx={{
//                                         borderRadius: '16px',
//                                         textTransform: 'none',
//                                         backgroundColor: selectedProvider === item.name ? theme.palette.primary.light + '33' : 'transparent',
//                                         color: selectedProvider === item.name ? theme.palette.primary.main : 'text.secondary',
//                                         '&:hover': {
//                                             backgroundColor: selectedProvider === item.name ? theme.palette.primary.light + '55' : theme.palette.grey[500] + '1A',
//                                         }
//                                     }}
//                                 >
//                                     <FiberManualRecordIcon sx={{ color: item.color, fontSize: '10px', mr: 0.75 }} />
//                                     <Typography variant="caption" sx={{ fontWeight: 500 }}>
//                                         {item.name}
//                                     </Typography>
//                                 </Button>
//                             ))}
//                         </Stack>
//                     </Box>
//                 </Box>

//                 <Box sx={{ flexGrow: 1, width: '100%' }}>
//                     <ResponsiveContainer width="100%" height="100%">
//                         <BarChart
//                             data={processedData}
//                             // FIX: Reduced the left margin to tighten the layout
//                             margin={{ top: 10, right: 10, left: 5, bottom: 0 }}
//                             barCategoryGap="63%"
//                         >
//                             <CartesianGrid strokeDasharray="4 4" stroke={theme.palette.divider} horizontal={true} vertical={false} />
//                             <XAxis dataKey="date" hide={true} />
//                             <YAxis
//                                 axisLine={false}
//                                 tickLine={false}
//                                 domain={yAxisDomain}
//                                 ticks={yAxisTicks}
//                                 tick={<CustomYAxisTick />}
//                             />
//                             <Tooltip
//                                 cursor={{ fill: 'transparent' }}
//                                 content={<CustomTooltip />}
//                             />
//                             <Bar dataKey="placeholder" stackId="a" fill={theme.palette.grey[200]} maxBarSize={15} />
//                             <Bar dataKey="Azure" stackId="a" fill="#94E9B8" maxBarSize={15} />
//                             <Bar dataKey="AWS" stackId="a" fill="#FF8900" maxBarSize={15} />
//                             <Bar dataKey="GCP" stackId="a" fill="rgba(0, 100, 255, 1)" maxBarSize={15} />
//                         </BarChart>
//                     </ResponsiveContainer>
//                 </Box>
//             </CardContent>
//         </Card>
//     );
// };

// export default CostCenterChart;